import { FieldError, RangeSlider, Typography } from '@/ui-kit/';
import styles from './setting-info-registration-form.module.scss';
import layoutStyles from '../../form-registration.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import {
    registrationStepFiveSchema,
    RegistrationStepFiveType
} from '@/schemes/registrationV2/registration-step-five-schema';
import clsx from 'clsx';
import { ZodEnum, ZodOptional } from 'zod';
import { eventsApi } from '@/api';
import { FieldBlock } from '@/components/field-block';
import { useTranslation } from 'react-i18next';
import { SignUpActions } from '@/components/sign-up-actions/sign-up-actions';

const DELAY_SEND_DATA = 1000;

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: { gender?: 'male' | 'female' }) => Promise<any>;
    nextStep: () => void;
}

export const SettingInfoRegistrationForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    useEffect(() => {
        callYaMetrika('new_dating_settings_screen');
        eventsApi.updateUserEvent('new_dating_settings_screen');
    }, []);

    const initialValues = useMemo(() => {
        const getMinAge = () => {
            if (!userData) return 18;
            return userData.min_age !== null ? (userData.min_age >= 60 ? 60 : userData.min_age) : 18;
        };

        const getMaxAge = () => {
            if (!userData) return 24;
            return userData.max_age !== null ? (userData.max_age >= 60 ? 60 : userData.max_age) : 24;
        };

        
        return {
            gender: userData?.gender,
            looks_for: userData?.looks_for || 'any',
            min_age: getMinAge(),
            max_age: getMaxAge(),
        };
    }, [userData]);

    const [isLooksForSelected, setIsLooksForSelected] = useState<Profile['looks_for']>(initialValues.looks_for);


    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepFiveType>({
        resolver: zodResolver(registrationStepFiveSchema),
        defaultValues: initialValues,
    });

    const { t } = useTranslation();

    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
    } = methods;

    const onSubmit = async (data: RegistrationStepFiveType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             * 
             *     await onUpdateUserData({ state: 'complete', ...data });
             * }
             */
            await onUpdateUserData(data);

            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_dating_settings_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeRangeAge = (value: any) => {
        methods.setValue('min_age', value[0]);
        methods.setValue('max_age', value[1]);
        eventsApi.updateUserEvent('registration_min_max_age');
        handleChangeUserDataDebounce({ min_age: value[0], max_age: value[1] });
    };

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const handleChangeGender = (value: any) => {
        methods.setValue('gender', value);
        handleChangeUserDataDebounce({ gender: value });
    };

    const handleChangeLooksFor = (value: any) => {
        methods.setValue('looks_for', value);
        setIsLooksForSelected(value);
        handleChangeUserDataDebounce({ looks_for: value });
    };

    useEffect(() => {
        eventsApi.updateUserEvent('registration_step_view_dating_settings_screen');
    }, [])

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('registration.dating_settings')}
                    </Typography>

                    <Controller
                        name="gender"
                        control={methods.control}
                        render={({ field: { onChange } }) =>
                            <FieldBlock>
                                <div className={styles.formLabel}>
                                    <Typography
                                        variant="label"
                                        fontWeight="bold"
                                        className={styles.formGenderTitle}
                                        isError={!!errors?.gender?.message}>
                                        {t('your_gender_secondary')}
                                    </Typography>
                                    <p className={styles.formGenderText}>
                                        {t('registration.you_can_change_it_once_later')}
                                    </p>
                                </div>
                                <div className={styles.genderWrapper}>
                                    <div
                                        className={clsx(styles.genderBtn, methods.getValues('gender') === 'female' && styles.genderBtnActive)}
                                        onClick={() => {
                                            const val = 'female'
                                            onChange(val)
                                            handleChangeGender(val)
                                        }}>
                                        {t('female')}
                                    </div>

                                    <div
                                        className={clsx(styles.genderBtn, methods.getValues('gender') === 'male' && styles.genderBtnActive)}
                                        onClick={() => {
                                            const val = 'male'
                                            onChange(val)
                                            handleChangeGender(val)
                                        }}>
                                        {t('male')}
                                    </div>
                                </div>
                            </FieldBlock>
                        } />

                    <FieldBlock>
                        <div className={styles.formGenderSearch}>
                            <div className={styles.formLabel}>
                                <Typography
                                    variant="label"
                                    fontWeight="bold"
                                    className={styles.formGenderTitle}
                                    isError={!!errors?.gender?.message}>
                                    {t('registration.who_show_you')}
                                </Typography>
                                <p className={styles.formGenderText}>
                                    {t('registration.сan_be_changed_at_any_time')}
                                </p>
                            </div>
                            <div className={styles.genderWrapper}>
                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'female' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('female')}>
                                    {t('registration.female_tertiary')}
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'male' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('male')}>
                                    {t('registration.male_tertiary')}
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'any' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('any')}>
                                    {t('optional')}
                                </div>
                            </div>
                        </div>
                    </FieldBlock>

                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="bold"
                                className={styles.formGenderTitle}
                                isError={!!errors?.gender?.message}>
                                {t('age_range')}
                            </Typography>
                        </div>
                        <RangeSlider
                            maxStart={initialValues?.max_age}
                            minStart={initialValues?.min_age}
                            onChange={handleChangeRangeAge} />
                        <FieldError message={errors?.max_age?.message || errors?.min_age?.message} />
                    </FieldBlock>
                </div>
                <SignUpActions className={layoutStyles.actions}>
                    <ButtonSubmitStep
                        isLoading={isLoading}
                        stepNumber={1}
                        isDisabled={!isValid} />
                </SignUpActions>
            </form>
        </FormProvider>
    );
};
